import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FounderBadge from '../components/FounderBadge';
import { ArrowLeft, Flag, Eye, EyeOff, Trash2, ShieldCheck, MessageSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminFellowshipModeration() {
  const [data, setData] = useState({ posts: [], comments: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // key -> bool

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/sanctuary/moderation');
      setData(data);
    } catch (err) {
      toast.error('Could not load moderation queue');
    } finally { setLoading(false); }
  };

  const act = async (kind, ids, action) => {
    const key = `${kind}-${ids.join('-')}-${action}`;
    if (action === 'delete' && !window.confirm(`Delete this ${kind}? This cannot be undone.`)) return;
    setBusy(prev => ({ ...prev, [key]: true }));
    try {
      const url = kind === 'post'
        ? `/admin/sanctuary/prayers/${ids[0]}/moderate`
        : `/admin/sanctuary/prayers/${ids[0]}/comments/${ids[1]}/moderate`;
      await api.post(url, { action });
      const verb = action === 'hide' ? 'Hidden' : action === 'restore' ? 'Restored' : 'Deleted';
      toast.success(`${verb}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(prev => ({ ...prev, [key]: false }));
    }
  };

  const total = (data.posts?.length || 0) + (data.comments?.length || 0);

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="moderation-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#B77BB020' }}>
            <ShieldCheck className="w-6 h-6" style={{ color: '#B77BB0' }} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#B77BB0', letterSpacing: '0.15em' }}>Fellowship</p>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Moderation Panel</h1>
          </div>
        </div>
        <p className="mb-8" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Any post or comment that has been flagged by members — or auto-hidden after 3 flags — appears here. You can restore, hide, or permanently delete.</p>

        <div className="mb-6 inline-flex items-center gap-3 px-4 py-2 rounded-full" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8' }} data-testid="moderation-count-pill">
          <FounderBadge compact />
          <span style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', fontSize: '13px' }}>
            {loading ? 'Loading…' : total === 0 ? 'All clear — no flagged items.' : `${total} item${total === 1 ? '' : 's'} awaiting your review`}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#B77BB0' }} /></div>
        ) : total === 0 ? (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="moderation-empty">
            <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: '#78A18E' }} />
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>Nothing to review right now. The circle is peaceful.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {data.posts?.length > 0 && (
              <section data-testid="moderation-posts-section">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                  <Flag className="w-4 h-4" style={{ color: '#E57A44' }} /> Flagged Posts ({data.posts.length})
                </h2>
                <ul className="space-y-3">
                  {data.posts.map(p => (
                    <li key={p.id} className="p-5 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid={`moderation-post-${p.id}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F6ECF3', color: '#B77BB0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '10px' }}>{p.post_type || 'prayer'}</span>
                        <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3E5DB', color: '#B34E1F', fontWeight: 700 }}>
                          <Flag className="w-3 h-3 inline mr-0.5" /> {p.flag_count} flag{p.flag_count === 1 ? '' : 's'}
                        </span>
                        {p.hidden && <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#EDE7E1', color: '#595553', fontWeight: 700 }}><EyeOff className="w-3 h-3 inline mr-0.5" /> Hidden</span>}
                        <span>· by {p.is_anonymous ? 'Anonymous' : p.user_name}</span>
                        <span>· {new Date(p.created_at).toLocaleString()}</span>
                      </div>
                      <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{p.title}</h3>
                      <p className="text-sm mt-1 mb-4 whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7 }}>{p.description}</p>
                      <ActionBar
                        hidden={p.hidden}
                        onRestore={() => act('post', [p.id], 'restore')}
                        onHide={() => act('post', [p.id], 'hide')}
                        onDelete={() => act('post', [p.id], 'delete')}
                        busy={busy}
                        keyBase={`post-${p.id}`}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.comments?.length > 0 && (
              <section data-testid="moderation-comments-section">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                  <MessageSquare className="w-4 h-4" style={{ color: '#5B7C99' }} /> Flagged Comments ({data.comments.length})
                </h2>
                <ul className="space-y-3">
                  {data.comments.map(c => (
                    <li key={c.id} className="p-5 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid={`moderation-comment-${c.id}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3E5DB', color: '#B34E1F', fontWeight: 700 }}>
                          <Flag className="w-3 h-3 inline mr-0.5" /> {c.flag_count} flag{c.flag_count === 1 ? '' : 's'}
                        </span>
                        {c.hidden && <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#EDE7E1', color: '#595553', fontWeight: 700 }}><EyeOff className="w-3 h-3 inline mr-0.5" /> Hidden</span>}
                        <span>· by {c.is_anonymous ? 'Anonymous' : c.user_name}</span>
                        <span>· on "{c.post_title}"</span>
                        <span>· {new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm mb-4 whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.7 }}>{c.content}</p>
                      <ActionBar
                        hidden={c.hidden}
                        onRestore={() => act('comment', [c.post_id, c.id], 'restore')}
                        onHide={() => act('comment', [c.post_id, c.id], 'hide')}
                        onDelete={() => act('comment', [c.post_id, c.id], 'delete')}
                        busy={busy}
                        keyBase={`comment-${c.id}`}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function ActionBar({ hidden, onRestore, onHide, onDelete, busy, keyBase }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`moderation-actions-${keyBase}`}>
      {hidden ? (
        <button onClick={onRestore} disabled={busy[`${keyBase}-restore`]}
          className="px-4 py-2 rounded-full flex items-center gap-2 text-sm disabled:opacity-50 transition hover:-translate-y-0.5"
          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }}
          data-testid={`${keyBase}-restore`}>
          <Eye className="w-4 h-4" /> Restore
        </button>
      ) : (
        <button onClick={onHide} disabled={busy[`${keyBase}-hide`]}
          className="px-4 py-2 rounded-full flex items-center gap-2 text-sm disabled:opacity-50 transition hover:-translate-y-0.5"
          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', color: '#B77BB0', border: '1.5px solid #B77BB0', fontWeight: 700 }}
          data-testid={`${keyBase}-hide`}>
          <EyeOff className="w-4 h-4" /> Hide
        </button>
      )}
      <button onClick={onDelete} disabled={busy[`${keyBase}-delete`]}
        className="px-4 py-2 rounded-full flex items-center gap-2 text-sm disabled:opacity-50 transition hover:-translate-y-0.5"
        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', color: '#B34E1F', border: '1.5px solid #B34E1F', fontWeight: 700 }}
        data-testid={`${keyBase}-delete`}>
        <Trash2 className="w-4 h-4" /> Delete
      </button>
    </div>
  );
}
